import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  Radio,
  RadioGroup,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { ControlledTooltip } from '@shared/components';

import { TSelectCaptionDialogState } from '../../types';
import DocumentContext from '../../contexts/DocumentContext.context';
import { OutRefBookmarkKind } from '../../tiptap/nodes/caption/constants';
import { captionLabelMap } from '@shared/i18n';
import { getCaptionFullText } from '@shared/backendFrontend/captionHelpersFe';

type TProps = {
  state?: TSelectCaptionDialogState;
  setState: (state: TSelectCaptionDialogState | undefined) => void;
};

type TCaptionItem = {
  id: string;
  text: string;
};

const useStyles = makeStyles({
  dialogContent: {
    minHeight: '10vh',
    maxHeight: '60vh',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  actions: {
    gridColumnStart: 1,
  },
  emptyMessage: {
    color: tokens.colorNeutralForeground3,
  },
  radioGroup: {
    overflow: 'hidden',
    width: '100%',
  },
  radio: {
    width: '100%',
    '& .fui-Radio__label': {
      overflow: 'hidden',
    },
  },
});

const TRUNCATE_THRESHOLD = 65;

const SelectCaptionDialog = ({ state, setState }: TProps) => {
  const { resolveCaptionLabel } = useContext(DocumentContext);
  const [selectedCaptionId, setSelectedCaptionId] = useState<string | null>(null);

  const classes = useStyles();

  // Reset selection when dialog opens
  useEffect(() => {
    if (state?.isOpen) setSelectedCaptionId(null);
  }, [state?.isOpen]);

  const captions = useMemo(() => {
    if (!state?.captionsMap) return [];

    const filtered: TCaptionItem[] = [];

    Object.entries(state.captionsMap).forEach(([id, entry]) => {
      if (entry.kind === state.captionKind) {
        filtered.push({ id, text: getCaptionFullText(entry, resolveCaptionLabel) });
      }
    });

    // Sort by prefix (e.g., "Table 1", "Table 2", etc.)
    return filtered.sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true }));
  }, [state?.captionsMap, state?.captionKind, resolveCaptionLabel]);

  const handleSelect = () => {
    if (!selectedCaptionId || !state) return;

    state.onSelect(selectedCaptionId);
    setState({ ...state, isOpen: false });
  };

  const handleClose = () => {
    if (state) setState({ ...state, isOpen: false });
  };

  const kindTitle = (captionLabelMap.EN[state?.captionKind ?? OutRefBookmarkKind.TABLE] ?? 'item').toLowerCase();

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Select {kindTitle} to reference
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {captions.length === 0 ? (
              <p className={classes.emptyMessage}>No {kindTitle}s found in this document.</p>
            ) : (
              <RadioGroup
                value={selectedCaptionId || ''}
                onChange={(_e, data) => setSelectedCaptionId(data.value)}
                className={classes.radioGroup}
              >
                {captions.map((caption) => (
                  <Radio
                    key={caption.id}
                    value={caption.id}
                    className={classes.radio}
                    label={
                      <ControlledTooltip
                        content={caption.text}
                        relationship="description"
                        positioning="after"
                        withArrow
                        enable={caption.text.length > TRUNCATE_THRESHOLD}
                      >
                        <span className="block truncate">{caption.text}</span>
                      </ControlledTooltip>
                    }
                  />
                ))}
              </RadioGroup>
            )}
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button appearance="primary" onClick={handleSelect} disabled={!selectedCaptionId || captions.length === 0}>
              Reference {kindTitle}
            </Button>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SelectCaptionDialog;
